import { useAppFeatures } from '@/hooks/useAppFeatures';
import { VerificationStatus } from '@operto/verifications-shared';
import { useAppStore } from '@store';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  useGetCompanyProviderQuery,
  useGetVerificationStatusQuery,
} from './api-slice';
import { isChekinStepRequired } from './utils';

// Notes on Chekin status and polling. Since the status is received via
// webhook from Chekin's side it can take some time to arrive in Dynamo.
// So polling occurs as below and immediately stops if we receive verified status.
const CHEKIN_STATUS_POLL_INTERVAL = 5000;
const CHEKIN_STATUS_POLL_ATTEMPTS = 30;

type UseChekinVerificationParams = {
  companyId?: string;
  reservationId?: string;
};

export const useChekinVerification = ({
  companyId,
  reservationId,
}: UseChekinVerificationParams) => {
  const { reservation, company } = useAppStore();
  const verificationCompanyId = companyId ?? company?.id ?? '';
  const verificationReservationId = reservationId ?? reservation?.id ?? '';

  const { isFeatureEnabled } = useAppFeatures();
  const verificationFeatureEnabled =
    isFeatureEnabled('verification', Number(verificationCompanyId)) &&
    isFeatureEnabled('verificationChekinSdk', Number(verificationCompanyId));

  const { data: companyProviderData, isLoading: companyProviderDataLoading } =
    useGetCompanyProviderQuery(
      { id: verificationCompanyId },
      { skip: !verificationCompanyId || !verificationFeatureEnabled },
    );

  const companyProviderEnabled = Boolean(companyProviderData?.data?.enabled);

  const {
    data: verificationStatusData,
    isLoading: verificationStatusLoading,
    isError: verificationStatusError,
    refetch,
  } = useGetVerificationStatusQuery(
    { reservationId: verificationReservationId },
    {
      skip:
        !verificationCompanyId ||
        !verificationReservationId ||
        !companyProviderEnabled ||
        !verificationFeatureEnabled,
    },
  );

  return {
    verificationStatus: verificationStatusData?.data
      ?.status as VerificationStatus,
    enabled: verificationFeatureEnabled && companyProviderEnabled,
    isLoading: companyProviderDataLoading || verificationStatusLoading,
    isError: verificationStatusError,
    refetch,
  };
};

export const useChekinStatus = ({
  companyId,
  reservationId,
}: UseChekinVerificationParams) => {
  const {
    enabled: isChekinEnabled,
    isError: isChekinError,
    isLoading: isChekinLoading,
    refetch,
    verificationStatus: chekinStatus,
  } = useChekinVerification({ companyId, reservationId });

  const isChekinRequired = isChekinStepRequired({
    enabled: isChekinEnabled,
    isError: isChekinError,
    isLoading: isChekinLoading,
    chekinStatus,
  });
  const isChekinRequiredRef = useRef(isChekinRequired);
  const chekinStatusPollRef = useRef<ReturnType<typeof setInterval>>();

  const stopChekinStatusPolling = useCallback(() => {
    if (chekinStatusPollRef.current) {
      clearInterval(chekinStatusPollRef.current);
      chekinStatusPollRef.current = undefined;
    }
  }, []);

  const startChekinStatusPolling = useCallback(() => {
    stopChekinStatusPolling();
    refetch();
    if (!isChekinRequiredRef.current) {
      return;
    }

    let attempts = 0;
    chekinStatusPollRef.current = setInterval(() => {
      if (!isChekinRequiredRef.current) {
        stopChekinStatusPolling();
        return;
      }
      attempts += 1;
      refetch();

      if (attempts >= CHEKIN_STATUS_POLL_ATTEMPTS) {
        stopChekinStatusPolling();
      }
    }, CHEKIN_STATUS_POLL_INTERVAL);
  }, [refetch, stopChekinStatusPolling]);

  useEffect(() => {
    isChekinRequiredRef.current = isChekinRequired;
    if (!isChekinRequired) {
      stopChekinStatusPolling();
    }
  }, [isChekinRequired, stopChekinStatusPolling]);

  useEffect(() => {
    return stopChekinStatusPolling;
  }, [stopChekinStatusPolling]);

  return useMemo(
    () => ({
      isChekinRequired,
      chekinStatus,
      isChekinEnabled,
      isChekinError,
      isChekinLoading,
      refetchChekinStatus: refetch,
      startChekinStatusPolling,
    }),
    [
      isChekinRequired,
      chekinStatus,
      isChekinEnabled,
      isChekinError,
      isChekinLoading,
      refetch,
      startChekinStatusPolling,
    ],
  );
};
